import {
  ArrowUpRight,
  Bell,
  Check,
  ChevronDown,
  Command,
  GitBranch,
  LogOut,
  Menu,
  Plus,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';
import type { FormEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import { api, ApiError, setWorkspaceScope } from '../../../api';
import { PROFILES, TIERS, type DemoProfile, type DemoTier } from '../demo/personaList';
import { endDemoSession, readDemoSession, startDemoSession } from '../sandbox/demoSession';
import { useWorkspaceActivity } from '../hooks/useWorkspaceActivity';
import { pageTheme } from '../../../pageThemes';
import { domainForPath } from '../../../shared/workspace/domains';
import { buildNav, NAV_SECTIONS, type NavItem } from '../../../shared/workspace/navigation';
import { CanonicalCopy } from '../../../shared/content/CanonicalCopy';
import { Brand } from '../../../shared/ui/Brand';
import { Button } from '../../../shared/ui/Button';
import { DatePicker } from '../../../shared/ui/DatePicker';
import { Empty } from '../../../shared/ui/Empty';
import { Field } from '../../../shared/ui/Field';
import { Loading } from '../../../shared/ui/Loading';
import { Modal } from '../../../shared/ui/Modal';
import type { Action, Context, Objective, Status, WorkspaceState } from '../../../types';

export interface WorkspaceContext {
  state: WorkspaceState;
  refresh: () => Promise<void>;
  notify: (message: string) => void;
  newObjective: () => void;
  newAction: (objectiveId?: string) => void;
  updateAction: (action: Action, status: Status, decision?: 'approve' | 'return') => Promise<void>;
}
export function useWorkspace() {
  return useOutletContext<WorkspaceContext>();
}
/** Flat [title, path, icon] list of every workspace tool, for search and the page title. */
const navigation = NAV_SECTIONS.flatMap((section) => section.items).map(
  (entry) => [entry.title, entry.to, entry.icon] as const,
);
export function WorkspaceShell() {
  const [state, setState] = useState<WorkspaceState | null>(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [modal, setModal] = useState<'objective' | 'action' | 'search' | null>(null);
  const [objectiveId, setObjectiveId] = useState('');
  const [mobile, setMobile] = useState(false);
  const [query, setQuery] = useState('');
  const activity = useWorkspaceActivity(state?.workspace.id);
  const navigate = useNavigate();
  // Demo visitors: which sample persona this tab is showing (null for real accounts).
  const demoSession = readDemoSession();
  const location = useLocation();
  // Switching persona re-enters the demo in-app (no page reload, which a static host would answer
  // with its own 404 on a deep link) and returns to the same page on the new persona's sandbox.
  const switchDemo = (profile: DemoProfile, tier: DemoTier) => {
    startDemoSession(profile, tier);
    navigate(`/demo/workspace?${new URLSearchParams({ profile, tier, next: location.pathname })}`, {
      replace: true,
    });
  };
  const refreshSequence = useRef(0);
  const displayedWorkspace = useRef<string | null>(null);
  const refresh = useCallback(async () => {
    const sequence = ++refreshSequence.current;
    try {
      const next = await api<WorkspaceState>('/state');
      if (sequence !== refreshSequence.current) return;
      if (displayedWorkspace.current && displayedWorkspace.current !== next.workspace.id) {
        setModal(null);
        setQuery('');
      }
      displayedWorkspace.current = next.workspace.id;
      setWorkspaceScope(next.workspace.id);
      setState(next);
      setError('');
    } catch (e) {
      if (sequence !== refreshSequence.current) return;
      if (e instanceof ApiError && e.status === 401)
        navigate(`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`, {
          replace: true,
        });
      else setError((e as Error).message);
      throw e;
    }
  }, [navigate, location.pathname, location.search]);
  useEffect(() => {
    void refresh().catch(() => {});
    const poll = () => {
      if (!document.hidden) void refresh().catch(() => {});
    };
    const timer = setInterval(poll, 15000);
    window.addEventListener('focus', poll);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', poll);
    };
  }, [refresh]);
  useEffect(() => {
    setMobile(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 4500);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setModal('search');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  async function updateAction(action: Action, status: Status, decision?: 'approve' | 'return') {
    try {
      await api(
        `/actions/${action.id}`,
        { version: action.version, status, ...(decision ? { decision } : {}) },
        'PATCH',
      );
      await refresh();
      setToast(
        decision === 'approve' ? 'Action approved. Your decision is recorded.' : 'Action updated.',
      );
    } catch (e) {
      await refresh().catch(() => {});
      throw e;
    }
  }
  if (!state)
    return error ? (
      <div className="error-page">
        <h1>We couldn’t load your workspace.</h1>
        <p role="alert">{error}</p>
        <Button onClick={() => void refresh().catch(() => {})}>Try again</Button>
      </div>
    ) : (
      <Loading />
    );
  const pending = state.actions.filter((a) => a.status === 'Needs review').length;
  const nav = buildNav(activity);
  const link = (entry: NavItem) => {
    const Icon = entry.icon;
    const count = entry.title === 'Today' ? pending : (entry.badge ?? 0);
    return (
      <NavLink
        key={entry.to}
        to={entry.to}
        end
        data-domain={entry.domain}
        className={({ isActive }) => (isActive ? 'sidebar-link active' : 'sidebar-link')}
      >
        <Icon size={18} strokeWidth={1.6} />
        {entry.title}
        {count > 0 && <span className="nav-count">{count}</span>}
      </NavLink>
    );
  };
  const current =
    [
      ...navigation,
      ['Companion', '/os/companion', Sparkles],
      ['Workflows', '/os/workflows', GitBranch],
      ['Governance', '/os/governance', ShieldCheck],
      ['Settings', '/os/settings', Settings2],
    ].find(([, path]) => path === location.pathname)?.[0] || 'Workspace';
  const ctx: WorkspaceContext = {
    state,
    refresh,
    notify: setToast,
    newObjective: () => setModal('objective'),
    newAction: (id) => {
      setObjectiveId(id || state.objectives[0]?.id || '');
      setModal('action');
    },
    updateAction,
  };
  const results = [
    ...state.objectives.map((o) => ({
      id: o.id,
      title: o.title,
      type: 'Objective',
      path: '/os/clarity',
    })),
    ...state.actions.map((a) => ({
      id: a.id,
      title: a.title,
      type: 'Action',
      path: '/os/consistency',
    })),
    ...navigation.map(([title, path]) => ({ id: path, title, type: 'Page', path })),
  ].filter((x) => x.title.toLowerCase().includes(query.toLowerCase()));
  return (
    <div className="workspace-layout" data-domain={domainForPath(location.pathname)}>
      <a className="skip-link" href="#workspace-main">
        Skip to workspace
      </a>
      {mobile && (
        <button
          className="sidebar-backdrop"
          aria-label="Close navigation"
          onClick={() => setMobile(false)}
        />
      )}
      <aside className={`sidebar ${mobile ? 'sidebar-open' : ''}`}>
        <div className="sidebar-brand">
          <Brand compact light />
          <button
            className="icon-button mobile-menu"
            aria-label="Close navigation"
            onClick={() => setMobile(false)}
          >
            <X size={20} />
          </button>
        </div>
        <label className="workspace-switch">
          <span className="workspace-icon">{state.workspace.name[0]}</span>
          <span>
            <strong>{state.workspace.name}</strong>
            <small>{state.workspace.context} workspace</small>
          </span>
          <select
            aria-label="Switch workspace"
            value={state.workspace.id}
            onChange={async (event) => {
              const workspaceId = event.target.value;
              ++refreshSequence.current;
              setModal(null);
              setQuery('');
              setState(null);
              try {
                await api('/workspace/switch', { workspaceId });
                await refresh();
              } catch (error) {
                setToast((error as Error).message);
                await refresh().catch(() => {});
              }
            }}
          >
            {state.workspaces.map((workspace) => (
              <option value={workspace.id} key={workspace.id}>
                {workspace.name}
              </option>
            ))}
          </select>
          <ChevronDown size={14} />
        </label>
        <div className="sidebar-label">YOUR OPERATING SPACE</div>
        <nav aria-label="Workspace navigation">
          {nav.sections.map((section) => (
            <div className="ws-nav-section" data-domain={section.domain} key={section.label}>
              <div className="ws-nav-heading">{section.label}</div>
              {section.items.map(link)}
            </div>
          ))}
          {nav.more.length > 0 && (
            <details className="ws-nav-more">
              <summary>
                More features <span className="ws-nav-more-count">{nav.more.length}</span>
              </summary>
              {nav.more.map(link)}
            </details>
          )}
          <div className="sidebar-divider" />
          <NavLink to="/os/companion" className="sidebar-link companion-link">
            <Sparkles size={18} />
            Companion<span className="new-label">GUIDED</span>
          </NavLink>
          <NavLink to="/os/workflows" className="sidebar-link">
            <GitBranch size={18} />
            Workflows
          </NavLink>
          <NavLink to="/os/governance" className="sidebar-link">
            <ShieldCheck size={18} />
            Governance
          </NavLink>
        </nav>
        <div className="sidebar-bottom">
          <div className="control-note">
            <ShieldCheck size={16} />
            <span>
              Your work. Your judgment.<small>You decide what happens next.</small>
            </span>
          </div>
          <NavLink to="/os/settings" end className="sidebar-link">
            <Settings2 size={18} />
            Settings
          </NavLink>
          <div className="profile-row">
            <span className="avatar">
              {state.user.name
                .split(' ')
                .map((x) => x[0])
                .slice(0, 2)
                .join('')}
            </span>
            <span>
              <strong>{state.user.name}</strong>
              <small>
                {state.user.demo ? 'Sample workspace' : `Workspace ${state.workspace.role}`}
              </small>
            </span>
            <button
              className="icon-button"
              aria-label="Sign out"
              onClick={async () => {
                try {
                  if (readDemoSession()) {
                    endDemoSession();
                    navigate('/demo');
                    return;
                  }
                  await api('/auth/logout', {});
                  navigate('/');
                } catch (e) {
                  setToast((e as Error).message);
                }
              }}
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>
      <div className="workspace-body">
        <header className="workspace-topbar">
          <div>
            <button
              className="icon-button mobile-menu"
              aria-label="Open workspace navigation"
              onClick={() => setMobile(true)}
            >
              <Menu size={20} />
            </button>
            <span className="breadcrumb-root">Workspace</span>
            <span className="breadcrumb-slash">/</span>
            <span>{String(current)}</span>
          </div>
          <div>
            <button className="search-button" onClick={() => setModal('search')}>
              <Search size={16} />
              <span>Search your workspace</span>
              <kbd>
                <Command size={10} /> K
              </kbd>
            </button>
            <Link
              className="notification-button icon-button"
              to="/os/today"
              aria-label={`${pending} actions need review`}
            >
              <Bell size={19} />
              {pending > 0 && <span />}
            </Link>
            <span className="topbar-divider" />
            <span className="avatar avatar-small">{state.user.name[0]}</span>
          </div>
        </header>
        {state.user.demo && (
          <div className="demo-banner">
            <span>
              <span className="gold-dot" /> Your own sample workspace. Explore freely; changes are
              saved in this session.
            </span>
            {demoSession && (
              <span className="demo-banner-controls">
                <select
                  name="demo-profile"
                  aria-label="Sample persona"
                  value={demoSession.profile}
                  onChange={(e) => switchDemo(e.target.value as DemoProfile, demoSession.tier)}
                >
                  {PROFILES.map((profile) => (
                    <option key={profile}>{profile}</option>
                  ))}
                </select>
                <select
                  name="demo-tier"
                  aria-label="Sample tier"
                  value={demoSession.tier}
                  onChange={(e) => switchDemo(demoSession.profile, e.target.value as DemoTier)}
                >
                  {TIERS.map((tier) => (
                    <option key={tier}>{tier}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => {
                    endDemoSession();
                    navigate('/demo');
                  }}
                >
                  Exit demo
                </button>
              </span>
            )}
            <Link to="/signup">
              Create your workspace <ArrowUpRight size={13} />
            </Link>
          </div>
        )}
        <main
          id="workspace-main"
          className="workspace-main"
          data-page-theme={pageTheme(location.pathname)}
          // Workspace tools own their hover and active states; the reading tint is for public pages.
          data-scroll-feedback="off"
          tabIndex={-1}
        >
          {error && (
            <p className="form-error" role="alert">
              {error} <button onClick={() => void refresh().catch(() => {})}>Retry</button>
            </p>
          )}
          <Outlet key={state.workspace.id} context={ctx} />
          {/* Collapsed by default — reference copy for the current route, kept for traceability
              against the copy master, not something a user should scroll past every visit.
              Expand only on demand. */}
          <details className="canonical-copy-disclosure">
            <summary>Getting started</summary>
            <CanonicalCopy path={location.pathname} embedded />
          </details>
        </main>
        <footer className="workspace-footer">
          <span>
            <span className="live-dot" /> Context connected. Judgment stays human.
          </span>
          <span>LAMID ONE · Development edition</span>
        </footer>
      </div>
      {toast && (
        <div className="toast" role="status">
          <Check size={17} />
          {toast}
          <button aria-label="Dismiss message" onClick={() => setToast('')}>
            <X size={14} />
          </button>
        </div>
      )}
      {modal === 'objective' && (
        <ObjectiveForm
          context={state.workspace.context}
          onClose={() => setModal(null)}
          onSaved={async () => {
            setModal(null);
            setToast('Your objective is ready. Define the next step.');
            await refresh().catch(() => {});
          }}
        />
      )}
      {modal === 'action' && (
        <ActionForm
          state={state}
          objectiveId={objectiveId}
          onClose={() => setModal(null)}
          onCreateObjective={() => setModal('objective')}
          onSaved={async () => {
            setModal(null);
            setToast('Next action added.');
            await refresh().catch(() => {});
          }}
        />
      )}
      {modal === 'search' && (
        <Modal
          title="Find your context"
          onClose={() => {
            setModal(null);
            setQuery('');
          }}
        >
          <div className="search-input">
            <Search size={19} />
            <input
              aria-label="Search objectives, actions, and pages"
              placeholder="Search objectives, actions, and pages…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="search-results">
            {results.slice(0, 12).map((result) => (
              <Link
                key={result.id}
                to={result.path}
                onClick={() => {
                  setModal(null);
                  setQuery('');
                }}
              >
                <span>
                  <small>{result.type}</small>
                  {result.title}
                </span>
                <ArrowUpRight size={16} />
              </Link>
            ))}
            {results.length === 0 && (
              <Empty title="No matching context">Try another word or create a new objective.</Empty>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
export function ObjectiveForm({
  context,
  objective,
  onSaved,
  onClose,
}: {
  context: Context;
  objective?: Objective;
  onSaved: () => Promise<void>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    data.title = String(data.title).toUpperCase();
    setBusy(true);
    setError('');
    try {
      await api(
        objective ? `/objectives/${objective.id}` : '/objectives',
        objective ? { ...data, version: objective.version } : data,
        objective ? 'PATCH' : 'POST',
      );
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={objective ? 'Edit goal' : 'What do you want to move forward?'} onClose={onClose}>
      <p className="modal-description">Start with an outcome. You can build the work around it.</p>
      <form onSubmit={submit}>
        <Field label="Your objective">
          <input
            name="title"
            required
            maxLength={500}
            defaultValue={objective?.title.toUpperCase()}
            autoCapitalize="characters"
            onChange={(e) => {
              const input = e.currentTarget;
              const start = input.selectionStart;
              const end = input.selectionEnd;
              const value = input.value;
              const upper = value.toUpperCase();
              if (upper !== value) {
                input.value = upper;
                if (start !== null && end !== null)
                  input.setSelectionRange(
                    value.slice(0, start).toUpperCase().length,
                    value.slice(0, end).toUpperCase().length,
                  );
              }
            }}
            placeholder="e.g. Launch a focused advisory service"
          />
        </Field>
        <Field label="Why it matters">
          <textarea
            name="description"
            defaultValue={objective?.description}
            maxLength={5000}
            rows={3}
            placeholder="Describe the situation and the change you want to make."
          />
        </Field>
        <div className="form-grid">
          <Field label="Context" hint="Set at signup and fixed from then on.">
            <div className="field-static">
              {context}
              <input type="hidden" name="context" value={context} />
            </div>
          </Field>
          <Field label="Priority">
            <select name="priority" defaultValue={objective?.priority ?? 'Medium'}>
              <option>High</option>
              <option>Medium</option>
              <option>Low</option>
            </select>
          </Field>
        </div>
        <Field label="What does success look like?">
          <input
            name="success"
            defaultValue={objective?.success}
            maxLength={5000}
            placeholder="An observable result, in your own words"
          />
        </Field>
        <Field label="Constraints to keep in view">
          <input
            name="constraints"
            defaultValue={objective?.constraints}
            maxLength={5000}
            placeholder="Time, resources, assumptions, or boundaries"
          />
        </Field>
        <Field label="Target date (optional)">
          <DatePicker name="targetDate" defaultValue={objective?.targetDate} />
        </Field>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Saving…' : objective ? 'Save changes' : 'Create objective'}
            <Plus size={16} />
          </Button>
        </div>
      </form>
    </Modal>
  );
}
function ActionForm({
  state,
  objectiveId,
  onSaved,
  onClose,
  onCreateObjective,
}: {
  state: WorkspaceState;
  objectiveId: string;
  onSaved: () => Promise<void>;
  onClose: () => void;
  onCreateObjective: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const data = {
      ...Object.fromEntries(form),
      requiresApproval: form.get('requiresApproval') === 'on',
      owner: state.user.name,
    };
    setBusy(true);
    try {
      await api('/actions', data);
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="Make the next step clear." onClose={onClose}>
      {state.objectives.length === 0 ? (
        <Empty
          title="Start with an objective"
          action={
            <Button onClick={onCreateObjective}>
              Create objective
              <Plus size={16} />
            </Button>
          }
        >
          Create an objective first so every action has a purpose.
        </Empty>
      ) : (
        <form onSubmit={submit}>
          <Field label="Action">
            <input
              name="title"
              required
              maxLength={500}
              placeholder="One concrete step you can take"
            />
          </Field>
          <Field label="Connected objective">
            <select name="objectiveId" defaultValue={objectiveId}>
              {state.objectives.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.title}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Due date (optional)">
            <DatePicker name="dueDate" />
          </Field>
          <Field label="Notes and acceptance criteria">
            <textarea
              name="notes"
              rows={3}
              maxLength={5000}
              placeholder="What needs to be true before this is complete?"
            />
          </Field>
          <label className="checkbox-field">
            <input name="requiresApproval" type="checkbox" />
            <span>
              Require a review before completion
              <small>You must explicitly approve this action after reviewing it.</small>
            </span>
          </label>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <div className="modal-actions">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Add next action'}
              <Plus size={16} />
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
